import { useEffect, useRef, useState, useCallback } from "react";
import { httpPost } from "../http";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  XIcon,
  ReceiptIcon,
  ScanIcon,
  KeyboardIcon,
  CameraIcon,
  WifiOff,
} from "lucide-react";
import { useToast } from "../components/Toast";
import QRCodeReader from "../utils/QRCodeReader";

declare global {
  interface Window {
    BarcodeDetector: any;
  }
}

type StateType = {
  shouldShowVideo: boolean;
  couponCode: string;
  inputGroups: string[];
};

export const PurchaseNew = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showError } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  const [state, setState] = useState<StateType>({
    shouldShowVideo: false,
    couponCode: "",
    inputGroups: Array(11).fill(""),
  });

  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const workWithBarcodeResult = useCallback((data: string) => {
    console.log(data);
    const parts = data.split("|");

    if (parts.length === 0) {
      return;
    }

    let couponCode = parts[0].replace(/\D/g, "");
    const groups = couponCode.match(/.{1,4}/g) || [];
    const paddedGroups = [...groups, ...Array(11 - groups.length).fill("")];

    setState((prevState) => ({
      ...prevState,
      couponCode,
      inputGroups: paddedGroups.slice(0, 11),
      shouldShowVideo: false,
    }));
  }, []);

  const handleToggleScanner = () => {
    const newShowVideo = !state.shouldShowVideo;
    setState({ ...state, shouldShowVideo: newShowVideo });
  };

  const handleGroupInput = (index: number, value: string) => {
    const sanitizedValue = value.replace(/\D/g, "").slice(0, 4);

    const newGroups = [...state.inputGroups];
    newGroups[index] = sanitizedValue;

    const newCouponCode = newGroups.join("");

    setState({
      ...state,
      inputGroups: newGroups,
      couponCode: newCouponCode,
    });

    if (sanitizedValue.length === 4 && index < 10) {
      const nextInput = document.getElementById(`group-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleGroupKeyDown = (index: number, event: React.KeyboardEvent) => {
    if (
      event.key === "Backspace" &&
      state.inputGroups[index] === "" &&
      index > 0
    ) {
      const prevInput = document.getElementById(`group-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleFormSubmit = (evt: any) => {
    evt.preventDefault();

    (async () => {
      try {
        const result = await httpPost("/api/v1/purchases", {
          tax_coupon_code: state.couponCode,
        });

        if (result.error === "logged_out") {
          navigate("/dash/logout");
        } else if (result.error) {
          showError("Falha ao registrar compra");
        } else if (!result.error) {
          navigate("/dash/purchases/new/success");
        }
      } finally {
      }
    })();
  };

  useEffect(() => {
    const updateOnlineStatus = () => {
      setIsOnline(navigator.onLine);
    };

    updateOnlineStatus();

    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);

    if (inputRef.current) {
      inputRef.current.focus();
    }

    const shouldAutoScan = searchParams.get("scan") === "true";
    if (shouldAutoScan) {
      setState((prev) => ({ ...prev, shouldShowVideo: true }));
    }

    return () => {
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
    };
  }, [searchParams]);

  const isScannerOpen = state.shouldShowVideo;

  return (
    <div className="container mx-auto px-4 pt-8 pb-24">
      <div className="mb-8">
        <Link
          to="/dash/purchases"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          <span className="font-medium">Voltar para Compras</span>
        </Link>
      </div>

      {!isScannerOpen ? (
        <>
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-blue-600 rounded-xl">
                <ReceiptIcon className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Nova Compra
                </h1>
                <p className="text-gray-600">
                  Registre uma nova compra através do código CFe
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <button
              type="button"
              onClick={() => {
                const firstInput = document.getElementById("group-0");
                firstInput?.focus();
              }}
              className="flex items-center justify-center gap-3 p-6 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-xl shadow-lg hover:from-gray-200 hover:to-gray-300 transition-all duration-200"
            >
              <KeyboardIcon className="h-8 w-8" />
              <div className="text-left">
                <div className="font-semibold text-lg">Digitar Código</div>
                <div className="text-gray-500 text-sm">Inserir manualmente</div>
              </div>
            </button>
            <button
              type="button"
              onClick={handleToggleScanner}
              className="flex items-center justify-center gap-3 p-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl shadow-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 transform hover:scale-105"
            >
              <CameraIcon className="h-8 w-8" />
              <div className="text-left">
                <div className="font-semibold text-lg">Escanear QR Code</div>
                <div className="text-blue-100 text-sm">Mais rápido e fácil</div>
              </div>
            </button>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-green-50 to-green-100 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-600 rounded-lg">
                  <ScanIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    Código do Cupom Fiscal
                  </h2>
                  <p className="text-green-700">
                    Digite o código de 44 dígitos
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <form onSubmit={handleFormSubmit} className="space-y-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-11 gap-2">
                    {state.inputGroups.map((group, index) => (
                      <div key={index} className="relative">
                        <input
                          id={`group-${index}`}
                          type="tel"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={group}
                          onChange={(e) =>
                            handleGroupInput(index, e.target.value)
                          }
                          onKeyDown={(e) => handleGroupKeyDown(index, e)}
                          maxLength={4}
                          className="w-full text-center py-3 px-2 text-lg font-mono bg-transparent border-0 border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none transition-colors"
                          placeholder="____"
                          autoComplete="off"
                        />
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      {state.couponCode.length}/44 dígitos inseridos
                    </div>
                    <button
                      type="button"
                      onClick={handleToggleScanner}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Usar câmera em vez disso
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={state.couponCode.length !== 44 || !isOnline}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 text-lg font-semibold text-white shadow-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-blue-600 disabled:hover:to-blue-700"
                >
                  {!isOnline ? (
                    <WifiOff className="h-6 w-6" />
                  ) : (
                    <ReceiptIcon className="h-6 w-6" />
                  )}
                  <span>
                    {!isOnline ? "Conecte-se à internet" : "Registrar Compra"}
                  </span>
                </button>
              </form>
            </div>
          </div>
        </>
      ) : (
        <div className="fixed inset-0 bg-black z-50">
          <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent z-10 p-4 pt-8">
            <div className="flex items-center justify-between text-white">
              <button
                onClick={handleToggleScanner}
                className="p-2 rounded-full bg-black/30 backdrop-blur-sm hover:bg-black/50 transition-colors"
              >
                <XIcon className="h-6 w-6" />
              </button>
              <div className="text-center">
                <h2 className="text-lg font-semibold">Escanear QR Code</h2>
                <p className="text-sm text-gray-300">
                  Posicione o código na área destacada
                </p>
              </div>
              <div className="w-10 h-10" />
            </div>
          </div>
          <div className="absolute inset-0">
            <QRCodeReader qrCodeSuccessCallback={workWithBarcodeResult} />
          </div>

          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 pb-12">
            <div className="text-center text-white space-y-4">
              {state.couponCode.length > 0 && (
                <div className="bg-black/30 backdrop-blur-sm rounded-xl p-4">
                  <div className="text-sm text-gray-300 mb-2">
                    Código detectado:
                  </div>
                  <div className="font-mono text-lg break-all">
                    {state.couponCode}
                  </div>
                  <div className="text-sm text-gray-300 mt-2">
                    {state.couponCode.length}/44 dígitos
                  </div>
                </div>
              )}

              <div className="flex items-center justify-center gap-2 text-sm text-gray-300">
                <CameraIcon className="h-4 w-4" />
                <span>Certifique-se de que a câmera tenha permissão</span>
              </div>

              <button
                onClick={handleToggleScanner}
                className="w-full py-4 bg-white/20 backdrop-blur-sm rounded-xl text-white font-semibold hover:bg-white/30 transition-colors"
              >
                Digitar código manualmente
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
